<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { fetchJson, formatCurrency } from '../helpers/orderHelpers';
import { fetchMarketplaceJson as fetchMarketplace } from 'dashboard/helpers/marketplaceApi';

const { t } = useI18n();
const route = useRoute();

const fetchMarketplaceJson = path =>
  fetchMarketplace(route.params.accountId, path);

// --- Tarih yardımcıları (Türkiye saat dilimi sabit UTC+3, DST yok) ---
const TR_OFFSET_MS = 3 * 60 * 60 * 1000;
const toDateStr = date => date.toISOString().slice(0, 10);
const todayTR = () => toDateStr(new Date(Date.now() + TR_OFFSET_MS));
const addDays = (dateStr, n) => {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return toDateStr(d);
};
const diffDays = (a, b) =>
  Math.round(
    (new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / 86400000
  );
const mondayOf = dateStr => {
  const day = new Date(`${dateStr}T00:00:00Z`).getUTCDay();
  return addDays(dateStr, day === 0 ? -6 : -(day - 1));
};
const firstOfMonth = dateStr => `${dateStr.slice(0, 7)}-01`;
const round2 = n => Math.round((n + Number.EPSILON) * 100) / 100;

const PRESETS = [
  'today',
  'yesterday',
  'this_week',
  'this_month',
  'last_7',
  'last_30',
];

const activePreset = ref('today');
const startDate = ref(todayTR());
const endDate = ref(todayTR());

const isLoading = ref(true);
const loadError = ref('');
const isExpanded = ref(true);

const ticimaxReport = ref(null);
const ticimaxPrevious = ref(null);
const shopifyReport = ref(null);
const shopifyPrevious = ref(null);
const trendyolReport = ref(null);
const trendyolPrevious = ref(null);
const trendyolModaxReport = ref(null);
const trendyolModaxPrevious = ref(null);
const agents = ref([]);
const previousRangeLabel = ref('');

// --- Rapor verisi: tum kanallar tek bir Rails endpoint'inden (yerel DB) geliyor ---
// Genel Durum sayfasiyla ayni sorgu/hesaplama mantigini kullandigi icin
// rakamlar artik her zaman tutarli (ModaXLarge Trendyol kanali dahil).
const loadReport = async () => {
  isLoading.value = true;
  loadError.value = '';
  const start = startDate.value;
  const end = endDate.value;
  const days = diffDays(start, end) + 1;
  const prevEnd = addDays(start, -1);
  const prevStart = addDays(prevEnd, -(days - 1));
  previousRangeLabel.value = `${prevStart} – ${prevEnd}`;

  const [currentResult, previousResult, agentsResult] =
    await Promise.allSettled([
      fetchMarketplaceJson(`reports/summary?start=${start}&end=${end}`),
      fetchMarketplaceJson(`reports/summary?start=${prevStart}&end=${prevEnd}`),
      fetchJson(`/ticimax-api/rapor?start=${start}&end=${end}`),
    ]);

  if (currentResult.status === 'fulfilled' && !currentResult.value.error) {
    ticimaxReport.value = currentResult.value.ticimax;
    shopifyReport.value = currentResult.value.shopify;
    trendyolReport.value = currentResult.value.trendyol_koza;
    trendyolModaxReport.value = currentResult.value.trendyol_modax;
  } else {
    ticimaxReport.value = null;
    shopifyReport.value = null;
    trendyolReport.value = null;
    trendyolModaxReport.value = null;
  }

  if (previousResult.status === 'fulfilled' && !previousResult.value.error) {
    ticimaxPrevious.value = previousResult.value.ticimax;
    shopifyPrevious.value = previousResult.value.shopify;
    trendyolPrevious.value = previousResult.value.trendyol_koza;
    trendyolModaxPrevious.value = previousResult.value.trendyol_modax;
  } else {
    ticimaxPrevious.value = null;
    shopifyPrevious.value = null;
    trendyolPrevious.value = null;
    trendyolModaxPrevious.value = null;
  }

  agents.value =
    agentsResult.status === 'fulfilled' ? agentsResult.value.agents || [] : [];

  if (
    !ticimaxReport.value &&
    !shopifyReport.value &&
    !trendyolReport.value &&
    !trendyolModaxReport.value
  ) {
    loadError.value = t('ORDERS.REPORT.ERROR');
  }
  isLoading.value = false;
};

const applyPreset = preset => {
  activePreset.value = preset;
  const today = todayTR();
  if (preset === 'today') {
    startDate.value = today;
    endDate.value = today;
  } else if (preset === 'yesterday') {
    const y = addDays(today, -1);
    startDate.value = y;
    endDate.value = y;
  } else if (preset === 'this_week') {
    startDate.value = mondayOf(today);
    endDate.value = today;
  } else if (preset === 'this_month') {
    startDate.value = firstOfMonth(today);
    endDate.value = today;
  } else if (preset === 'last_7') {
    startDate.value = addDays(today, -6);
    endDate.value = today;
  } else if (preset === 'last_30') {
    startDate.value = addDays(today, -29);
    endDate.value = today;
  }
  loadReport();
};

const onCustomDateChange = () => {
  if (endDate.value < startDate.value) endDate.value = startDate.value;
  activePreset.value = 'custom';
  loadReport();
};

onMounted(loadReport);

// --- Birlesik (Koza Butik + ModaXLarge + Trendyol) toplamlar ---
const sumField = (field, ...sources) =>
  round2(
    (ticimaxReport.value?.[field] || 0) +
      sources.reduce((acc, s) => acc + (s?.[field] || 0), 0)
  );

const combined = computed(() => ({
  orderCount:
    (ticimaxReport.value?.orderCount || 0) +
    (shopifyReport.value?.orderCount || 0) +
    (trendyolReport.value?.orderCount || 0) +
    (trendyolModaxReport.value?.orderCount || 0),
  totalRevenue: sumField(
    'totalRevenue',
    shopifyReport.value,
    trendyolReport.value,
    trendyolModaxReport.value
  ),
  netRevenue: sumField(
    'netRevenue',
    shopifyReport.value,
    trendyolReport.value,
    trendyolModaxReport.value
  ),
  cancelledCount:
    (ticimaxReport.value?.cancelledCount || 0) +
    (shopifyReport.value?.cancelledCount || 0) +
    (trendyolReport.value?.cancelledCount || 0) +
    (trendyolModaxReport.value?.cancelledCount || 0),
  cancelledTotal: sumField(
    'cancelledTotal',
    shopifyReport.value,
    trendyolReport.value,
    trendyolModaxReport.value
  ),
  codCount:
    (ticimaxReport.value?.codCount || 0) +
    (shopifyReport.value?.codCount || 0) +
    (trendyolReport.value?.codCount || 0) +
    (trendyolModaxReport.value?.codCount || 0),
  codTotal: sumField(
    'codTotal',
    shopifyReport.value,
    trendyolReport.value,
    trendyolModaxReport.value
  ),
  returnCount:
    (ticimaxReport.value?.returnCount || 0) +
    (shopifyReport.value?.returnCount || 0) +
    (trendyolReport.value?.returnCount || 0) +
    (trendyolModaxReport.value?.returnCount || 0),
  returnTotal: sumField(
    'returnTotal',
    shopifyReport.value,
    trendyolReport.value,
    trendyolModaxReport.value
  ),
  deliveryFailedCount:
    (ticimaxReport.value?.deliveryFailedCount || 0) +
    (shopifyReport.value?.deliveryFailedCount || 0) +
    (trendyolReport.value?.deliveryFailedCount || 0) +
    (trendyolModaxReport.value?.deliveryFailedCount || 0),
  deliveryFailedTotal: sumField(
    'deliveryFailedTotal',
    shopifyReport.value,
    trendyolReport.value,
    trendyolModaxReport.value
  ),
}));

const combinedPrevious = computed(() => ({
  orderCount:
    (ticimaxPrevious.value?.orderCount || 0) +
    (shopifyPrevious.value?.orderCount || 0) +
    (trendyolPrevious.value?.orderCount || 0) +
    (trendyolModaxPrevious.value?.orderCount || 0),
  totalRevenue:
    (ticimaxPrevious.value?.totalRevenue || 0) +
    (shopifyPrevious.value?.totalRevenue || 0) +
    (trendyolPrevious.value?.totalRevenue || 0) +
    (trendyolModaxPrevious.value?.totalRevenue || 0),
}));

// Trendyol karti Koza + ModaX trendyol kanallarinin toplamini gosterir.
const trendyolCombined = computed(() => ({
  totalRevenue: round2(
    (trendyolReport.value?.totalRevenue || 0) +
      (trendyolModaxReport.value?.totalRevenue || 0)
  ),
  orderCount:
    (trendyolReport.value?.orderCount || 0) +
    (trendyolModaxReport.value?.orderCount || 0),
}));

const avgOrderValue = computed(() => {
  const nonCancelled =
    combined.value.orderCount - combined.value.cancelledCount;
  return nonCancelled ? round2(combined.value.netRevenue / nonCancelled) : 0;
});

const cancelRate = computed(() =>
  combined.value.orderCount
    ? round2((combined.value.cancelledCount / combined.value.orderCount) * 100)
    : 0
);

const codRate = computed(() =>
  combined.value.orderCount
    ? round2((combined.value.codCount / combined.value.orderCount) * 100)
    : 0
);

const returnRate = computed(() =>
  combined.value.orderCount
    ? round2((combined.value.returnCount / combined.value.orderCount) * 100)
    : 0
);

const deliveryFailedRate = computed(() =>
  combined.value.orderCount
    ? round2(
        (combined.value.deliveryFailedCount / combined.value.orderCount) * 100
      )
    : 0
);

const trendPct = (current, previous) => {
  if (!previous) return current > 0 ? 100 : null;
  return round2(((current - previous) / previous) * 100);
};

const revenueTrend = computed(() =>
  trendPct(combined.value.totalRevenue, combinedPrevious.value.totalRevenue)
);
const orderCountTrend = computed(() =>
  trendPct(combined.value.orderCount, combinedPrevious.value.orderCount)
);

// Sohbet/temsilci uzerinden olusturulan siparislerin toplam ciroya orani —
// destek ekibinin satislara katkisini gosteren bir gostergedir.
const agentAssistedTotal = computed(() =>
  round2(agents.value.reduce((sum, a) => sum + a.totalRevenue, 0))
);
const agentAssistedShare = computed(() =>
  combined.value.totalRevenue
    ? round2((agentAssistedTotal.value / combined.value.totalRevenue) * 100)
    : 0
);

// --- Gunluk ciro trendi (uc kaynagin toplamı) ---
const dailyChart = computed(() => {
  const map = {};
  (ticimaxReport.value?.dailyBreakdown || []).forEach(d => {
    map[d.date] = (map[d.date] || 0) + d.total;
  });
  (shopifyReport.value?.dailyBreakdown || []).forEach(d => {
    map[d.date] = (map[d.date] || 0) + d.total;
  });
  (trendyolReport.value?.dailyBreakdown || []).forEach(d => {
    map[d.date] = (map[d.date] || 0) + d.total;
  });
  (trendyolModaxReport.value?.dailyBreakdown || []).forEach(d => {
    map[d.date] = (map[d.date] || 0) + d.total;
  });
  const dates = Object.keys(map).sort();
  const max = Math.max(1, ...dates.map(d => map[d]));
  return dates.map(date => ({
    date,
    total: round2(map[date]),
    pct: Math.max(2, (map[date] / max) * 100),
  }));
});

const formatShortDate = iso => {
  const [, m, d] = iso.split('-');
  return `${d}.${m}`;
};

const formatPct = value => {
  if (value === null) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};
</script>

<template>
  <section class="border-b border-n-weak flex-shrink-0 bg-n-solid-1">
    <header class="flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
      <div class="flex items-center gap-2 min-w-0">
        <span class="size-5 i-lucide-bar-chart-3 text-n-brand flex-shrink-0" />
        <div class="min-w-0">
          <h2 class="text-sm font-semibold truncate text-n-slate-12">
            {{ t('ORDERS.REPORT.TITLE') }}
          </h2>
          <p class="text-xs truncate text-n-slate-10">
            {{ t('ORDERS.REPORT.SUBTITLE') }}
          </p>
        </div>
      </div>
      <button
        type="button"
        class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg text-n-slate-11 outline outline-1 outline-n-weak hover:bg-n-alpha-1 flex-shrink-0"
        @click="isExpanded = !isExpanded"
      >
        <span
          class="size-3.5"
          :class="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        />
        {{ isExpanded ? t('ORDERS.REPORT.HIDE') : t('ORDERS.REPORT.SHOW') }}
      </button>
    </header>

    <div v-if="isExpanded" class="px-4 pb-4 sm:px-6">
      <!-- Tarih filtresi -->
      <div class="flex flex-wrap items-center gap-2 mb-4">
        <button
          v-for="preset in PRESETS"
          :key="preset"
          type="button"
          class="px-3 py-1.5 text-xs font-medium rounded-full transition-colors"
          :class="
            activePreset === preset
              ? 'bg-n-brand text-white'
              : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
          "
          @click="applyPreset(preset)"
        >
          {{ t(`ORDERS.REPORT.PRESETS.${preset.toUpperCase()}`) }}
        </button>
        <div class="flex items-center gap-1.5 ml-1">
          <input
            v-model="startDate"
            type="date"
            :max="endDate"
            class="px-2 py-1 text-xs border rounded-lg bg-n-alpha-1 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            @change="onCustomDateChange"
          />
          <span class="text-xs text-n-slate-10">–</span>
          <input
            v-model="endDate"
            type="date"
            :min="startDate"
            :max="todayTR()"
            class="px-2 py-1 text-xs border rounded-lg bg-n-alpha-1 border-n-weak text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            @change="onCustomDateChange"
          />
        </div>
      </div>

      <div
        v-if="isLoading"
        class="flex items-center justify-center gap-2 py-10 text-n-slate-11"
      >
        <Spinner />
        <p class="text-sm">{{ t('ORDERS.REPORT.LOADING') }}</p>
      </div>

      <div
        v-else-if="loadError"
        class="flex items-center justify-center gap-2 py-10 text-sm text-n-ruby-10"
      >
        <span class="size-4 i-lucide-alert-triangle" />
        {{ loadError }}
      </div>

      <template v-else>
        <!-- Ozet kartlari -->
        <div
          class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8"
        >
          <!-- Genel toplam ciro -->
          <div
            class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
          >
            <p class="text-xs text-n-slate-10">
              {{ t('ORDERS.REPORT.CARDS.TOTAL_REVENUE') }}
            </p>
            <p class="mt-1 text-lg font-semibold text-n-slate-12">
              {{ formatCurrency(combined.totalRevenue) }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ t('ORDERS.REPORT.CARDS.NET_REVENUE') }}:
              {{ formatCurrency(combined.netRevenue) }}
            </p>
            <p
              v-if="revenueTrend !== null"
              class="flex items-center gap-1 mt-1 text-xs font-medium"
              :class="revenueTrend >= 0 ? 'text-n-teal-11' : 'text-n-ruby-11'"
            >
              <span
                class="size-3"
                :class="
                  revenueTrend >= 0
                    ? 'i-lucide-trending-up'
                    : 'i-lucide-trending-down'
                "
              />
              {{ formatPct(revenueTrend) }}
              <span class="text-n-slate-10">{{
                t('ORDERS.REPORT.VS_PREVIOUS')
              }}</span>
            </p>
          </div>

          <!-- Koza Butik toplam -->
          <div
            class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-amber-6"
          >
            <p class="flex items-center gap-1.5 text-xs text-n-slate-10">
              <span class="rounded-full size-2 bg-n-amber-9" />
              {{ t('ORDERS.REPORT.CARDS.TICIMAX_TOTAL') }}
            </p>
            <p class="mt-1 text-lg font-semibold text-n-slate-12">
              {{
                formatCurrency(ticimaxReport ? ticimaxReport.totalRevenue : 0)
              }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ ticimaxReport ? ticimaxReport.orderCount : 0 }}
              {{ t('ORDERS.REPORT.CARDS.ORDERS_SUFFIX') }}
            </p>
          </div>

          <!-- ModaXLarge toplam -->
          <div
            class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-teal-6"
          >
            <p class="flex items-center gap-1.5 text-xs text-n-slate-10">
              <span class="rounded-full size-2 bg-n-teal-9" />
              {{ t('ORDERS.REPORT.CARDS.SHOPIFY_TOTAL') }}
            </p>
            <p class="mt-1 text-lg font-semibold text-n-slate-12">
              {{
                formatCurrency(shopifyReport ? shopifyReport.totalRevenue : 0)
              }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ shopifyReport ? shopifyReport.orderCount : 0 }}
              {{ t('ORDERS.REPORT.CARDS.ORDERS_SUFFIX') }}
            </p>
          </div>

          <!-- Trendyol toplam -->
          <div
            class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-iris-6"
          >
            <p class="flex items-center gap-1.5 text-xs text-n-slate-10">
              <span class="rounded-full size-2 bg-n-iris-9" />
              {{ t('ORDERS.REPORT.CARDS.TRENDYOL_TOTAL') }}
            </p>
            <p class="mt-1 text-lg font-semibold text-n-slate-12">
              {{ formatCurrency(trendyolCombined.totalRevenue) }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ trendyolCombined.orderCount }}
              {{ t('ORDERS.REPORT.CARDS.ORDERS_SUFFIX') }}
              <span
                v-if="!trendyolReport && !trendyolModaxReport"
                class="text-n-ruby-10"
                >—</span>
            </p>
          </div>

          <!-- Toplam siparis -->
          <div
            class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
          >
            <p class="text-xs text-n-slate-10">
              {{ t('ORDERS.REPORT.CARDS.ORDER_COUNT') }}
            </p>
            <p class="mt-1 text-lg font-semibold text-n-slate-12">
              {{ combined.orderCount }}
            </p>
            <p
              v-if="orderCountTrend !== null"
              class="flex items-center gap-1 mt-1 text-xs font-medium"
              :class="
                orderCountTrend >= 0 ? 'text-n-teal-11' : 'text-n-ruby-11'
              "
            >
              <span
                class="size-3"
                :class="
                  orderCountTrend >= 0
                    ? 'i-lucide-trending-up'
                    : 'i-lucide-trending-down'
                "
              />
              {{ formatPct(orderCountTrend) }}
              <span class="text-n-slate-10">{{
                t('ORDERS.REPORT.VS_PREVIOUS')
              }}</span>
            </p>
          </div>

          <!-- Ortalama sepet tutari -->
          <div
            class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
          >
            <p class="text-xs text-n-slate-10">
              {{ t('ORDERS.REPORT.CARDS.AVG_ORDER') }}
            </p>
            <p class="mt-1 text-lg font-semibold text-n-slate-12">
              {{ formatCurrency(avgOrderValue) }}
            </p>
          </div>

          <!-- Kapida odeme -->
          <div
            class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
          >
            <p class="text-xs text-n-slate-10">
              {{ t('ORDERS.REPORT.CARDS.COD') }}
            </p>
            <p class="mt-1 text-lg font-semibold text-n-slate-12">
              %{{ codRate.toFixed(1) }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ formatCurrency(combined.codTotal) }}
            </p>
          </div>

          <!-- Iptal/iade orani -->
          <div
            class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-ruby-6"
          >
            <p class="text-xs text-n-slate-10">
              {{ t('ORDERS.REPORT.CARDS.CANCELLED') }}
            </p>
            <p class="mt-1 text-lg font-semibold text-n-ruby-11">
              %{{ cancelRate.toFixed(1) }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ combined.cancelledCount }}
              {{ t('ORDERS.REPORT.CARDS.ORDERS_SUFFIX') }} ·
              {{ formatCurrency(combined.cancelledTotal) }}
            </p>
          </div>

          <!-- Teslim sonrasi iade orani -->
          <div
            class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-amber-6"
          >
            <p class="text-xs text-n-slate-10">
              {{ t('ORDERS.REPORT.CARDS.RETURN_RATE') }}
            </p>
            <p class="mt-1 text-lg font-semibold text-n-amber-11">
              %{{ returnRate.toFixed(1) }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ combined.returnCount }}
              {{ t('ORDERS.REPORT.CARDS.ORDERS_SUFFIX') }} ·
              {{ formatCurrency(combined.returnTotal) }}
            </p>
          </div>

          <!-- Teslim edilemedi / kapida odeme reddi orani -->
          <div
            class="p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-amber-6"
          >
            <p class="text-xs text-n-slate-10">
              {{ t('ORDERS.REPORT.CARDS.DELIVERY_FAILED_RATE') }}
            </p>
            <p class="mt-1 text-lg font-semibold text-n-amber-11">
              %{{ deliveryFailedRate.toFixed(1) }}
            </p>
            <p class="mt-1 text-xs text-n-slate-10">
              {{ combined.deliveryFailedCount }}
              {{ t('ORDERS.REPORT.CARDS.ORDERS_SUFFIX') }} ·
              {{ formatCurrency(combined.deliveryFailedTotal) }}
            </p>
          </div>
        </div>

        <!-- Gunluk ciro trendi -->
        <div
          v-if="dailyChart.length > 1"
          class="p-3 mt-4 rounded-xl bg-n-solid-2 outline outline-1 outline-n-weak"
        >
          <p class="mb-3 text-xs font-medium text-n-slate-11">
            {{ t('ORDERS.REPORT.CHART.TITLE') }}
          </p>
          <div class="flex items-end gap-1.5 h-28">
            <div
              v-for="day in dailyChart"
              :key="day.date"
              class="flex flex-col items-center justify-end flex-1 h-full gap-1 group"
            >
              <div
                class="w-full transition-colors rounded-t bg-n-brand/30 group-hover:bg-n-brand/60"
                :style="{ height: `${day.pct}%` }"
                :title="`${day.date}: ${formatCurrency(day.total)}`"
              />
              <span class="text-[10px] text-n-slate-10">{{
                formatShortDate(day.date)
              }}</span>
            </div>
          </div>
        </div>

        <!-- Temsilci bazinda satislar -->
        <div class="mt-4">
          <div class="flex items-center justify-between mb-2">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ t('ORDERS.REPORT.AGENTS.TITLE') }}
            </h3>
            <p v-if="agents.length" class="text-xs text-n-slate-10">
              {{
                t('ORDERS.REPORT.AGENTS.SHARE', {
                  pct: agentAssistedShare.toFixed(1),
                })
              }}
            </p>
          </div>
          <div
            v-if="!agents.length"
            class="px-3 py-3 text-xs rounded-xl text-n-slate-10 bg-n-solid-2 outline outline-1 outline-n-weak"
          >
            {{ t('ORDERS.REPORT.AGENTS.EMPTY') }}
          </div>
          <div
            v-else
            class="overflow-x-auto rounded-xl outline outline-1 outline-n-weak"
          >
            <table class="w-full text-sm border-collapse">
              <thead class="text-xs uppercase bg-n-solid-2 text-n-slate-10">
                <tr>
                  <th class="px-3 py-2 text-start font-medium">
                    {{ t('ORDERS.REPORT.AGENTS.AGENT') }}
                  </th>
                  <th class="px-3 py-2 text-end font-medium">
                    {{ t('ORDERS.REPORT.AGENTS.ORDER_COUNT') }}
                  </th>
                  <th
                    class="hidden px-3 py-2 text-end font-medium sm:table-cell"
                  >
                    {{ t('ORDERS.REPORT.AGENTS.KOZA') }}
                  </th>
                  <th
                    class="hidden px-3 py-2 text-end font-medium sm:table-cell"
                  >
                    {{ t('ORDERS.REPORT.AGENTS.MODAX') }}
                  </th>
                  <th class="px-3 py-2 text-end font-medium">
                    {{ t('ORDERS.REPORT.AGENTS.TOTAL') }}
                  </th>
                  <th
                    class="hidden px-3 py-2 text-end font-medium md:table-cell"
                  >
                    {{ t('ORDERS.REPORT.AGENTS.AVG') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="agent in agents"
                  :key="agent.agentName"
                  class="border-t border-n-weak"
                >
                  <td class="px-3 py-2 font-medium text-n-slate-12">
                    {{ agent.agentName }}
                  </td>
                  <td class="px-3 py-2 text-end text-n-slate-11">
                    {{ agent.orderCount }}
                  </td>
                  <td
                    class="hidden px-3 py-2 text-end sm:table-cell text-n-slate-11"
                  >
                    {{ agent.ticimax.count }} ·
                    {{ formatCurrency(agent.ticimax.total) }}
                  </td>
                  <td
                    class="hidden px-3 py-2 text-end sm:table-cell text-n-slate-11"
                  >
                    {{ agent.shopify.count }} ·
                    {{ formatCurrency(agent.shopify.total) }}
                  </td>
                  <td class="px-3 py-2 font-medium text-end text-n-slate-12">
                    {{ formatCurrency(agent.totalRevenue) }}
                  </td>
                  <td
                    class="hidden px-3 py-2 text-end md:table-cell text-n-slate-11"
                  >
                    {{ formatCurrency(agent.avgOrderValue) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="mt-1.5 text-[11px] text-n-slate-10">
            {{ t('ORDERS.REPORT.AGENTS.NOTE') }}
          </p>
        </div>
      </template>
    </div>
  </section>
</template>
